<script>
import throttle from 'lodash.throttle';
import { scrollTop } from '@/utils/dom';
import { HAS_INTERNAL_NAV } from '@/store/types';
import { mapActions } from 'vuex';

// a reference to add/remove event listeners
let scrollHandler;

export default {
  name: 'InternalNavbar',

  props: {
    navigation: {
      required: true,
      type: Array,
    },

    navClass: {
      type: String,
      default: 'internal-nav',
    },

    autoHide: {
      type: Boolean,
      default: false,
    },

    hideOffset: {
      type: Number,
      default: 100,
    },
  },

  data() {
    return {
      show: true,
      lastScrollYPos: 0,
    };
  },

  mounted() {
    this.setHasInternalNav(true);

    if (!this.autoHide) {
      return false;
    }

    scrollHandler = throttle(this.hideHandler.bind(this, scrollTop), 250);
    window.addEventListener('scroll', scrollHandler);
    /* istanbul ignore next */
    // for testing purposes
    return scrollHandler;
  },

  beforeDestroy() {
    this.setHasInternalNav(false);

    if (!this.autoHide) {
      return false;
    }

    window.removeEventListener('scroll', scrollHandler);
  },

  methods: {
    ...mapActions({
      setHasInternalNav: HAS_INTERNAL_NAV,
    }),
    /* eslint-disable no-shadow */
    hideHandler(scrollTop) {
      const scrollYPos = scrollTop(window);

      if (this.lastScrollYPos > scrollYPos) {
        this.show = true;
      } else if (scrollYPos > this.hideOffset) {
        this.show = false;
      }

      this.lastScrollYPos = scrollYPos;
    },

    subIsActive(input) {
      return this.$route.path.indexOf(`/${input}/`) > -1;
    },
  },

  render() {
    const items = this.navigation.map(getNavItem, this);

    const nav = (
      <nav class={[this.navClass, 'z-20']} v-show={this.show}>
        <ul class={`${this.navClass}__list`}>{items}</ul>
      </nav>
    );

    function getNavItem({
      to,
      text,
      disabled = false,
      isNew = false,
      warnBadge = false,
      children = [],
      rootPath,
    }) {
      let content;

      if (!children.length) {
        content = getRouterLink.call(this, {
          to,
          text,
          disabled,
          isNew,
          warnBadge,
        });
      } else {
        const subItems = children.map(
          ({ visible = true, ...item }) => (
            <el-dropdown-item v-show={visible} key={item.text}>
              {getRouterLink.call(this, item)}
            </el-dropdown-item>
          ),
          this,
        );

        content = (
          <el-dropdown>
            <span
              class={[
                'el-dropdown-link',
                `${this.navClass}__link`,
                { active: this.subIsActive(rootPath) },
              ]}>
              <span class="inline-center">{text}</span>{' '}
              <i class="el-icon-caret-bottom inline-center"></i>
            </span>
            <el-dropdown-menu slot="dropdown" class={`${this.navClass}__menu`}>
              {subItems}
            </el-dropdown-menu>
          </el-dropdown>
        );
      }

      const isNewClassName = isNew ? `${this.navClass}__item--spaced` : '';

      return (
        <li class={`${this.navClass}__item ${isNewClassName}`} key={text}>
          {' '}
          {content}{' '}
        </li>
      );
    }

    function getRouterLink({ to, text, disabled, isNew, warnBadge }) {
      let content;

      if (disabled) {
        content = (
          <span
            class={`${this.navClass}__link ${this.navClass}__link--disabled`}>
            {text}
          </span>
        );
      } else {
        const isNewContent = isNew ? (
          <span class={`${this.navClass}__new`}>{this.$t('NEW')}</span>
        ) : (
          ''
        );

        const warnBadgeContent = warnBadge ? (
          <i class="el-icon-warning internal-nav__warn-badge" />
        ) : (
          ''
        );

        content = (
          <span>
            <router-link
              class={`${this.navClass}__link`}
              to={to}
              activeClass={'active'}>
              {text}
              {isNewContent}
              {warnBadgeContent}
            </router-link>
          </span>
        );
      }
      return content;
    }

    if (this.autoHide) {
      return <transition name="slide">{nav}</transition>;
    }

    return nav;
  },
};
</script>

<style lang="scss">
.internal-nav {
  position: fixed;
  left: 0;
  right: 0;
  top: $lb-navbar-height; // ui-toolbar height
  border-bottom: 1px solid $lb-off-canvas-border-color;
  background-color: $lb-white;
  height: $lb-internal-navbar-height;

  @include breakpoint(large) {
    // to override the inline style that is given via JavaScript
    display: block !important;
    position: static;

    margin-top: 1rem;
    margin-bottom: 2rem;

    background-color: transparent;
  }

  &__list {
    display: flex;
    height: 100%;
    flex-flow: row nowrap;

    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;

    list-style: none;
    margin: 0;

    @include breakpoint(large) {
      margin-left: -0.5rem;
      margin-right: -0.5rem;
    }
  }

  &__warn-badge {
    color: $lb-color-alert;
    font-size: $lb-side-nav-font-size;
    margin: 0 $global-margin * 0.3;
  }

  &__item {
    white-space: nowrap;

    .el-dropdown {
      display: block;
      margin: 0;
      font-size: $lb-global-font-size;
    }

    &--spaced {
      margin-right: calc($global-margin / 2);
      margin-left: 0;
      @include rtl() {
        margin-left: calc($global-margin / 2);
        margin-right: 0;
      }
    }

    &:last-child {
      .internal-nav__link {
        margin-right: 0;
        margin-left: 0.5rem;

        @include rtl() {
          margin-left: 0;
          margin-right: 0.5rem;
        }
      }
    }
  }

  &__new {
    position: absolute;
    font-size: 70%;
    color: $lb-color-alert;
    margin-left: $global-margin / 4;
    bottom: 20px;
    right: -13px;
    left: auto;
    @include rtl() {
      left: -13px;
      right: auto;
      margin-right: $global-margin / 4;
    }
  }

  &__link {
    position: relative;
    display: block;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    padding: 0.5rem rem-calc(7);
    cursor: pointer;
    height: 100%;
    color: $lb-darker-gray;
    transition: box-shadow 0.1s ease-in;

    &:focus,
    &:hover,
    &.active {
      color: $lb-color-primary;
    }

    &.active {
      box-shadow: inset 0 -1.5px;
    }

    &--disabled {
      cursor: default;

      color: $lb-medium-gray;

      &:focus,
      &:hover,
      &.active {
        color: $lb-medium-gray;
      }
    }
  }

  &__menu {
    @include rtl() {
      text-align: right;
      color: #f00;
    }

    .el-dropdown-menu__item {
      cursor: auto;
    }

    .internal-nav__link {
      margin: 0;
      padding: 0;
    }
  }
}

// slide animations
.slide {
  @include breakpoint(medium down) {
    /* Enter and leave animations can use different */
    /* durations and timing functions.              */
    &-enter-active {
      transition: transform 0.15s ease;
    }

    &-leave-active {
      transition: transform 0.35s ease;
    }

    &-enter,
    &-leave-to {
      transform: translateY(-100%);
    }
  }
}
</style>
